import { useEffect, useMemo, useRef, useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { Progress, InfoCard } from '@backstage/core-components';
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';

const ATLAS_PROJECT_ID = 55300; // MGMTAPPPRYMANAGEMENT/olympus/atlas
const MAX_RENDERABLE_NODES = 1500;

// Misma paleta categorica (Tableau10) que usa graphify al generar graph.html
const PALETTE = [
  '#4E79A7', '#F28E2B', '#E15759', '#76B7B2', '#59A14F',
  '#EDC948', '#B07AA1', '#FF9DA7', '#9C755F', '#BAB0AC',
];

function communityColor(community: number | undefined): string {
  return PALETTE[(community ?? 0) % PALETTE.length];
}

interface GraphNode {
  id: string;
  label: string;
  community?: number;
  community_name?: string;
  file_type?: string;
  source_file?: string;
}

interface GraphLink {
  source: string;
  target: string;
  relation?: string;
  weight?: number;
}

interface GraphManifest {
  nodes: GraphNode[];
  links: GraphLink[];
}

interface LegendEntry {
  community: number;
  name: string;
  color: string;
  count: number;
}

export const isAtlasGraphAvailable = (entity: Entity): boolean =>
  Boolean(entity.metadata.annotations?.['olympus.io/atlas-graph']);

function extractRepoPath(fullUrl: string): string | null {
  // ".../-/raw/<ref>/<repo-relative-path>"
  const match = fullUrl.match(/\/-\/(?:blob|raw)\/[^/]+\/(.+)$/);
  return match ? match[1] : null;
}

export const EntityAtlasGraphContent = () => {
  const { entity } = useEntity();
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [manifest, setManifest] = useState<GraphManifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [selectedNeighbors, setSelectedNeighbors] = useState<GraphNode[]>([]);
  const [hiddenCommunities, setHiddenCommunities] = useState<Set<number>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);
  const networkRef = useRef<Network | null>(null);
  const nodesDataRef = useRef<DataSet<any> | null>(null);
  const nodeByIdRef = useRef<Map<string, GraphNode>>(new Map());
  const degreeRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const annotationUrl = entity.metadata.annotations?.['olympus.io/atlas-graph'];
        if (!annotationUrl) {
          setError('No hay grafo disponible para este componente.');
          return;
        }
        const repoPath = extractRepoPath(annotationUrl);
        if (!repoPath) {
          setError('No se pudo interpretar la ruta del grafo.');
          return;
        }
        const proxyBase = await discoveryApi.getBaseUrl('proxy');
        const url = `${proxyBase}/gitlabci/projects/${ATLAS_PROJECT_ID}/repository/files/${encodeURIComponent(
          repoPath,
        )}/raw?ref=main`;
        const res = await fetchApi.fetch(url);
        if (!res.ok) {
          setError(`No se pudo cargar el grafo (status ${res.status}).`);
          return;
        }
        const data = (await res.json()) as GraphManifest;
        setManifest(data);
      } catch (e: any) {
        setError(e.message ?? 'Error desconocido cargando el grafo.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [entity, fetchApi, discoveryApi]);

  const legend = useMemo<LegendEntry[]>(() => {
    if (!manifest) return [];
    const byCommunity = new Map<number, { name: string; count: number }>();
    manifest.nodes.forEach(n => {
      const c = n.community ?? 0;
      const existing = byCommunity.get(c);
      if (existing) {
        existing.count += 1;
      } else {
        byCommunity.set(c, { name: n.community_name ?? `Comunidad ${c}`, count: 1 });
      }
    });
    return Array.from(byCommunity.entries())
      .map(([community, v]) => ({ community, name: v.name, count: v.count, color: communityColor(community) }))
      .sort((a, b) => b.count - a.count);
  }, [manifest]);

  const searchResults = useMemo<GraphNode[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || !manifest) return [];
    return manifest.nodes.filter(n => n.label.toLowerCase().includes(q)).slice(0, 20);
  }, [searchQuery, manifest]);

  const focusNode = (nodeId: string) => {
    const network = networkRef.current;
    if (!network) return;
    network.focus(nodeId, { scale: 1.4, animation: true as any });
    network.selectNodes([nodeId]);
    showInfo(nodeId);
    setSearchQuery('');
  };

  const showInfo = (nodeId: string) => {
    const node = nodeByIdRef.current.get(nodeId);
    if (!node) return;
    setSelectedNode(node);
    const network = networkRef.current;
    const neighborIds = network ? network.getConnectedNodes(nodeId) as string[] : [];
    setSelectedNeighbors(
      neighborIds.map(id => nodeByIdRef.current.get(id)).filter((n): n is GraphNode => Boolean(n)),
    );
  };

  const toggleCommunity = (community: number) => {
    if (!manifest || !nodesDataRef.current) return;
    const nowHidden = !hiddenCommunities.has(community);
    const next = new Set(hiddenCommunities);
    if (nowHidden) next.add(community);
    else next.delete(community);
    setHiddenCommunities(next);

    const updates = manifest.nodes
      .filter(n => (n.community ?? 0) === community)
      .map(n => ({ id: n.id, hidden: nowHidden }));
    nodesDataRef.current.update(updates);
  };

  useEffect(() => {
    if (!manifest || !containerRef.current) return undefined;
    if (manifest.nodes.length > MAX_RENDERABLE_NODES) return undefined;
    const container = containerRef.current;

    const degree: Record<string, number> = {};
    manifest.links.forEach(l => {
      degree[l.source] = (degree[l.source] || 0) + 1;
      degree[l.target] = (degree[l.target] || 0) + 1;
    });
    degreeRef.current = degree;
    const maxDeg = Math.max(...Object.values(degree), 1);

    const nodeMap = new Map<string, GraphNode>();
    manifest.nodes.forEach(n => nodeMap.set(n.id, n));
    nodeByIdRef.current = nodeMap;

    const nodes = new DataSet(
      manifest.nodes.map(n => {
        const deg = degree[n.id] || 0;
        const color = communityColor(n.community);
        return {
          id: n.id,
          label: n.label,
          title: `${n.label}\n${n.source_file ?? ''}`,
          color: {
            background: color,
            border: color,
            highlight: { background: '#ffffff', border: color },
          },
          size: 10 + (deg / maxDeg) * 30,
          font: { size: 13, color: '#e6edf3', strokeWidth: 2, strokeColor: '#0d1117' },
          shape: 'dot',
        };
      }),
    );
    nodesDataRef.current = nodes;

    const edges = new DataSet(
      manifest.links.map((l, i) => ({
        id: i,
        from: l.source,
        to: l.target,
        title: l.relation,
        arrows: 'to',
        color: { color: '#30363d', highlight: '#58a6ff', opacity: 0.5 },
        width: Math.min(1 + (l.weight ?? 1) * 0.3, 4),
      })),
    );

    const network = new Network(
      container,
      { nodes, edges },
      {
        physics: {
          solver: 'forceAtlas2Based',
          forceAtlas2Based: { gravitationalConstant: -60, springLength: 100, springConstant: 0.08 },
          stabilization: { iterations: 150 },
        },
        interaction: { hover: true, tooltipDelay: 100, hideEdgesOnDrag: true },
        edges: { smooth: { enabled: true, type: 'continuous', roundness: 0.3 } },
      },
    );
    networkRef.current = network;

    network.once('stabilizationIterationsDone', () => {
      network.setOptions({ physics: { enabled: false } });
    });

    network.on('click', params => {
      if (params.nodes && params.nodes.length > 0) {
        showInfo(String(params.nodes[0]));
      }
    });

    return () => {
      network.destroy();
      networkRef.current = null;
      nodesDataRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest]);

  if (loading) {
    return (
      <InfoCard title="Code Graph">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Code Graph">
        <p style={{ padding: 16, color: '#666' }}>{error}</p>
      </InfoCard>
    );
  }

  if (manifest && manifest.nodes.length > MAX_RENDERABLE_NODES) {
    const annotationUrl = entity.metadata.annotations?.['olympus.io/atlas-graph'] ?? '';
    const reportUrl = annotationUrl.replace(/graph\.json$/, 'GRAPH_REPORT.md');
    return (
      <InfoCard title="Code Graph">
        <p style={{ padding: 16 }}>
          Este grafo tiene {manifest.nodes.length.toLocaleString()} nodos — demasiado grande para
          visualizar interactivamente sin afectar el rendimiento del navegador.
        </p>
        <p style={{ padding: '0 16px 16px' }}>
          <a href={reportUrl} target="_blank" rel="noopener noreferrer">
            Ver GRAPH_REPORT.md
          </a>
        </p>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Code Graph" noPadding>
      <div style={{ display: 'flex', height: '75vh', background: '#0d1117' }}>
        <div ref={containerRef} style={{ flex: 1, minWidth: 0 }} />
        <div
          style={{
            width: 280,
            flexShrink: 0,
            background: '#161b22',
            borderLeft: '1px solid #30363d',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            color: '#e6edf3',
            fontSize: 13,
          }}
        >
          <div style={{ padding: 12, borderBottom: '1px solid #30363d', position: 'relative' }}>
            <input
              type="text"
              placeholder="Buscar nodos..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                background: '#0d1117',
                border: '1px solid #30363d',
                color: '#e6edf3',
                padding: '7px 10px',
                borderRadius: 6,
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            {searchResults.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  left: 12,
                  right: 12,
                  top: 48,
                  maxHeight: 160,
                  overflowY: 'auto',
                  background: '#161b22',
                  border: '1px solid #30363d',
                  borderRadius: 6,
                  zIndex: 5,
                }}
              >
                {searchResults.map(n => (
                  <div
                    key={n.id}
                    onClick={() => focusNode(n.id)}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontSize: 12,
                      borderLeft: `3px solid ${communityColor(n.community)}`,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {n.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ padding: 14, borderBottom: '1px solid #30363d', minHeight: 130 }}>
            <h4 style={{ fontSize: 12, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '.05em', margin: '0 0 8px' }}>
              Info del nodo
            </h4>
            {!selectedNode && <span style={{ color: '#555', fontStyle: 'italic', fontSize: 12 }}>Clic en un nodo para inspeccionarlo</span>}
            {selectedNode && (
              <div style={{ fontSize: 12, lineHeight: 1.6 }}>
                <div><b>{selectedNode.label}</b></div>
                <div>Tipo: {selectedNode.file_type ?? 'desconocido'}</div>
                <div>Comunidad: {selectedNode.community_name ?? selectedNode.community}</div>
                <div>Archivo: {selectedNode.source_file ?? '-'}</div>
                <div>Conexiones: {degreeRef.current[selectedNode.id] ?? 0}</div>
                {selectedNeighbors.length > 0 && (
                  <>
                    <div style={{ marginTop: 8, color: '#8b949e', fontSize: 11 }}>Vecinos ({selectedNeighbors.length})</div>
                    <div style={{ maxHeight: 140, overflowY: 'auto', marginTop: 4 }}>
                      {selectedNeighbors.map(nb => (
                        <div
                          key={nb.id}
                          onClick={() => focusNode(nb.id)}
                          style={{
                            padding: '2px 6px',
                            margin: '2px 0',
                            borderRadius: 3,
                            cursor: 'pointer',
                            fontSize: 12,
                            borderLeft: `3px solid ${communityColor(nb.community)}`,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {nb.label}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
            <h4 style={{ fontSize: 12, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '.05em', margin: '0 0 10px' }}>
              Comunidades
            </h4>
            {legend.map(entry => {
              const hidden = hiddenCommunities.has(entry.community);
              return (
                <div
                  key={entry.community}
                  onClick={() => toggleCommunity(entry.community)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 0',
                    cursor: 'pointer',
                    fontSize: 12,
                    opacity: hidden ? 0.35 : 1,
                  }}
                >
                  <input type="checkbox" checked={!hidden} readOnly style={{ flexShrink: 0 }} />
                  <div style={{ width: 12, height: 12, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.name}
                  </span>
                  <span style={{ color: '#666', fontSize: 11 }}>{entry.count}</span>
                </div>
              );
            })}
          </div>

          <div style={{ padding: '10px 14px', borderTop: '1px solid #30363d', fontSize: 11, color: '#555' }}>
            {manifest?.nodes.length} nodos · {manifest?.links.length} edges · {legend.length} comunidades
          </div>
        </div>
      </div>
    </InfoCard>
  );
};
